import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config';
import '../styles/SharedList.css';

const SimpleSharedList = () => {
  const [sharedList, setSharedList] = useState(null);
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({ name: '', quantity: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);
  const [groupBy, setGroupBy] = useState('day'); // 'day' or 'alphabetical'
  const checkIntervalRef = useRef(null);
  
  // Check for updates interval (in milliseconds)
  const CHECK_INTERVAL = 30000; // 30 seconds
  
  // Fetch the shared list data
  const fetchSharedList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/simple-shared-list/`);
      const data = await response.json();
      
      if (response.ok) {
        setSharedList(data);
        setItems(JSON.parse(data.items_json || '[]'));
        setLastCheck(new Date());
      } else {
        setError(data.error || 'Failed to fetch shared list');
      }
    } catch (err) {
      console.error("Error fetching shared list:", err);
      setError('Network error fetching shared list');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Check for updates without showing loading indicator
  const checkForUpdates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/simple-shared-list/`);
      const data = await response.json();
      
      if (response.ok) {
        // Check if the last_updated timestamp is different
        if (sharedList && data.last_updated !== sharedList.last_updated) {
          setSharedList(data);
          setItems(JSON.parse(data.items_json || '[]'));
          setLastCheck(new Date());
        } else {
          // Just update the last check time
          setLastCheck(new Date());
        }
      }
    } catch (err) {
      console.error("Error checking for updates:", err);
      // Don't show error for background updates
    }
  }, [sharedList]);
  
  useEffect(() => {
    // Load the shared list on component mount
    fetchSharedList();
    
    // Set up interval to check for updates
    checkIntervalRef.current = setInterval(() => {
      checkForUpdates();
    }, CHECK_INTERVAL);
    
    // Clean up interval on component unmount
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [fetchSharedList, checkForUpdates, CHECK_INTERVAL]);
  
  // Save the current state of the shopping list
  const saveSharedList = async () => {
    try {
      setSaveStatus('saving');
      const response = await fetch(`${API_URL}/simple-shared-list/save_list/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items_json: JSON.stringify(items),
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSharedList(data);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus('error');
        setError(data.error || 'Failed to save shared list');
      }
    } catch (err) {
      console.error("Error saving shared list:", err);
      setSaveStatus('error');
      setError('Network error saving shared list');
    }
  };
  
  // Handle input change for new item form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewItem({
      ...newItem,
      [name]: value,
    });
  };
  
  // Add a new item to the list
  const handleAddItem = (e) => {
    e.preventDefault();
    if (!newItem.name.trim()) return;
    
    // Create the new item
    const newItemWithDetails = {
      id: Date.now(), // Use timestamp as temporary ID
      name: newItem.name,
      quantity: newItem.quantity || '',
      completed: false,
      day: 'Ungrouped', // Default day
      meal_type: 'Other', // Default meal type
    };
    
    // Update state
    setItems([...items, newItemWithDetails]);
    setNewItem({ name: '', quantity: '' });
    
    // Automatically save after adding
    setTimeout(() => saveSharedList(), 500);
  };
  
  // Toggle item completion status
  const handleToggleComplete = (id) => {
    const updatedItems = items.map(item => 
      item.id === id ? { ...item, completed: !item.completed } : item
    );
    setItems(updatedItems);
    
    // Don't save immediately on toggle to avoid too many saves
  };
  
  // Delete an item from the list
  const handleDeleteItem = (id) => {
    const updatedItems = items.filter(item => item.id !== id);
    setItems(updatedItems);
    
    // Automatically save after deleting
    setTimeout(() => saveSharedList(), 500);
  };
  
  // Group items based on the selected grouping method
  const groupedItems = items.reduce((acc, item) => {
    if (!item || typeof item !== 'object') {
      return acc; // Skip invalid items
    }
    
    if (groupBy === 'alphabetical') {
      // Group by first letter
      const firstChar = (item.name && typeof item.name === 'string') 
        ? item.name.charAt(0).toUpperCase() 
        : '#';
      
      if (!acc[firstChar]) {
        acc[firstChar] = [];
      }
      
      acc[firstChar].push(item);
    } else {
      // Group by day and meal type
      const day = (item.day && typeof item.day === 'string') ? item.day : 'Ungrouped';
      
      if (!acc[day]) {
        acc[day] = {};
      }
      
      const mealType = (item.meal_type && typeof item.meal_type === 'string') ? item.meal_type : 'Other';
      
      if (!acc[day][mealType]) {
        acc[day][mealType] = [];
      }
      
      acc[day][mealType].push(item);
    }
    
    return acc;
  }, {});
  
  // Format time for display
  const formatTime = (date) => {
    if (!date) return '';
    const now = new Date();
    const timeDiff = now - date;
    
    if (timeDiff < 60000) {
      return 'Just now';
    } else if (timeDiff < 3600000) {
      const minutes = Math.floor(timeDiff / 60000);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleTimeString();
    }
  };
  
  if (loading) {
    return <div className="loading">Loading shared shopping list...</div>;
  }
  
  return (
    <div className="shared-list-container">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h2>Shared Shopping List</h2>
          <div className="list-actions">
            <select 
              className="form-select me-2"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
            >
              <option value="day">Group by Day/Meal</option>
              <option value="alphabetical">Group Alphabetically</option>
            </select>
            <button 
              className="btn btn-primary"
              onClick={saveSharedList}
              disabled={saveStatus === 'saving'}
            >
              {saveStatus === 'saving' ? 'Saving...' : 'Save List'}
            </button>
          </div>
        </div>
        
        <div className="card-body">
          {error && <div className="alert alert-danger">{error}</div>}
          
          {saveStatus === 'saved' && (
            <div className="alert alert-success">Shopping list saved successfully!</div>
          )}
          
          <div className="list-info mb-3">
            <p>
              <strong>Last Updated:</strong> {sharedList?.last_updated_formatted || 'Never'}
              {lastCheck && <span className="text-muted ms-3">(Last checked: {formatTime(lastCheck)})</span>}
            </p>
            <p className="text-muted">
              This is a shared shopping list that can be accessed by anyone. Changes will be visible to all users after saving.
            </p>
          </div>
          
          <form onSubmit={handleAddItem} className="mb-4">
            <div className="row g-3">
              <div className="col-sm-5">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Item name"
                  name="name"
                  value={newItem.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="col-sm-5">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Quantity (e.g., 2 lbs, 3 pcs)"
                  name="quantity"
                  value={newItem.quantity}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-sm-2">
                <button type="submit" className="btn btn-success w-100">
                  Add Item
                </button>
              </div>
            </div>
          </form>
          
          {groupBy === 'alphabetical' ? (
            // Alphabetical view
            <div className="items-container">
              {Object.keys(groupedItems).sort().map(letter => (
                <div key={letter} className="letter-section">
                  <h4 className="letter-header">{letter}</h4>
                  {groupedItems[letter].map(item => (
                    <div 
                      key={item.id} 
                      className={`item-row ${item.completed ? 'item-completed' : ''}`}
                    >
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={item.completed}
                          onChange={() => handleToggleComplete(item.id)}
                          id={`item-${item.id}`}
                        />
                        <label 
                          className="form-check-label" 
                          htmlFor={`item-${item.id}`}
                        >
                          {item.name} {item.quantity && <span className="item-quantity">({item.quantity})</span>}
                        </label>
                      </div>
                      <button 
                        className="btn btn-sm btn-danger item-delete-btn" 
                        onClick={() => handleDeleteItem(item.id)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            // Day and meal type view
            <div className="items-container">
              {Object.keys(groupedItems)
                .sort((a, b) => {
                  // Custom sort for days
                  const dayOrder = {
                    'Monday': 1,
                    'Tuesday': 2,
                    'Wednesday': 3,
                    'Thursday': 4,
                    'Friday': 5,
                    'Saturday': 6,
                    'Sunday': 7,
                    'Ungrouped': 8
                  };
                  return (dayOrder[a] || 99) - (dayOrder[b] || 99);
                })
                .map(day => (
                  <div key={day} className="day-section">
                    <h4 className="day-header">{day}</h4>
                    {Object.keys(groupedItems[day])
                      .sort((a, b) => {
                        // Custom sort for meal types
                        const mealOrder = {
                          'Breakfast': 1,
                          'Lunch': 2,
                          'Dinner': 3,
                          'Other': 4
                        };
                        return (mealOrder[a] || 99) - (mealOrder[b] || 99);
                      })
                      .map(mealType => (
                        <div key={`${day}-${mealType}`} className="meal-section">
                          <h5 className="meal-header">{mealType}</h5>
                          {groupedItems[day][mealType].map(item => (
                            <div 
                              key={item.id} 
                              className={`item-row ${item.completed ? 'item-completed' : ''}`}
                            >
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={item.completed}
                                  onChange={() => handleToggleComplete(item.id)}
                                  id={`item-${item.id}`}
                                />
                                <label 
                                  className="form-check-label" 
                                  htmlFor={`item-${item.id}`}
                                >
                                  {item.name} {item.quantity && <span className="item-quantity">({item.quantity})</span>}
                                </label>
                              </div>
                              <button 
                                className="btn btn-sm btn-danger item-delete-btn" 
                                onClick={() => handleDeleteItem(item.id)}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      ))}
                  </div>
                ))}
            </div>
          )}
          
          {items.length === 0 && (
            <div className="text-center text-muted p-5">
              <p>No items in the shopping list yet. Add some items to get started!</p>
            </div>
          )}
        </div>
        
        <div className="card-footer d-flex justify-content-between">
          <button className="btn btn-primary" onClick={saveSharedList}>
            {saveStatus === 'saving' ? 'Saving...' : 'Save Changes'}
          </button>
          <button className="btn btn-outline-secondary" onClick={fetchSharedList}>
            Refresh List
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimpleSharedList; 